// src/views/DeleteAccountPage.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trash2, Mail, CheckCircle } from 'lucide-react';

const DeleteAccountPage = () => {
  const [copied, setCopied] = useState(false);

  const email = 'support@spatchy.co';
  const subject = 'Account Deletion Request';
  const body = `Hi Spatchy Team,

I would like to request the deletion of my account and all associated data.

Name: [Your Name]
Account Email: [Your Account Email]

Please confirm once my account has been deleted.

Thank you.`;

  const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Helmet>
        <title>Delete Your Account | Spatchy</title>
        <meta name="description" content="Request deletion of your Spatchy account and associated data." />
      </Helmet>

      {/* Header */}
      <div className="border-b border-white/10 bg-slate-900/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Trash2 size={24} className="text-red-400" />
            <h1 className="text-3xl font-bold text-white">Delete Your Account</h1>
          </div>
          <p className="text-slate-400 mt-2 text-sm">
            Request deletion of your Spatchy account and associated data.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-8">

          {/* How to request */}
          <div>
            <h2 className="text-xl font-semibold text-white mb-3">How to Request Account Deletion</h2>
            <p className="text-slate-400 leading-relaxed">
              To request deletion of your Spatchy account and all associated data, send an email to our support team. 
              We will process your request within <strong className="text-white">30 days</strong> and send you a confirmation once complete.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-4">
            {[
              { step: '1', text: 'Click the button below to open a pre-filled email in your mail app' },
              { step: '2', text: 'Fill in your name and the email address associated with your Spatchy account' },
              { step: '3', text: 'Send the email — we\'ll confirm deletion within 30 days' },
            ].map(({ step, text }) => (
              <div key={step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                  {step}
                </div>
                <p className="text-slate-300 pt-1">{text}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={mailtoLink}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
            >
              <Mail size={18} />
              Send Deletion Request Email
            </a>
            <button
              onClick={handleCopy}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors border border-white/10"
            >
              {copied ? <CheckCircle size={18} className="text-green-400" /> : <Mail size={18} />}
              {copied ? 'Copied!' : `Copy email: ${email}`}
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 pt-8 space-y-6">

            {/* What gets deleted */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-3">What Gets Deleted</h2>
              <ul className="space-y-2 text-slate-400">
                {[
                  'Your account login and authentication credentials',
                  'Your personal profile information (name, email, phone)',
                  'All jobs, invoices, quotes, and customer records associated with your organization',
                  'Team member accounts linked to your organization',
                  'All uploaded files and documents',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What may be retained */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-3">What May Be Retained</h2>
              <ul className="space-y-2 text-slate-400">
                {[
                  'Billing and transaction records required by law (retained for up to 7 years)',
                  'Anonymized, aggregated data that cannot be linked back to you',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-slate-500 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="bg-white/5 rounded-xl p-5">
              <p className="text-slate-400 text-sm">
                Questions about your data? Contact us at{' '}
                <a href={`mailto:${email}`} className="text-blue-400 hover:text-blue-300 font-medium">
                  {email}
                </a>
                {' '}or review our{' '}
                <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300 font-medium">
                  Privacy Policy
                </a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;